import React from 'react';
import SEOHead from '../components/SEOHead';
import { getCurrentDate } from '../utils/dateUtils';
import './Privacy.css';

const Privacy = () => {
  return (
    <>
      <SEOHead 
        title="Privacy Policy - YT DOWN | Your Privacy is Our Priority"
        description="Read YT DOWN's privacy policy. Learn how we protect your data when downloading YouTube and TikTok videos. No data collection, no tracking, 100% secure and private. Your downloads are anonymous."
        keywords="privacy policy, yt down privacy, youtube downloader privacy, tiktok downloader privacy, data protection, secure downloads, anonymous downloads, no tracking"
        url="https://digimac.pro/privacy"
      />
      <section id="privacy" className="privacy-section">
      <div className="privacy-container">
        {/* Hero Section */}
        <div className="privacy-hero">
          <h1 className="privacy-title">Privacy Policy</h1>
          <p className="privacy-subtitle">
            Your Privacy is Our Priority
          </p>
          <p className="last-updated">Last Updated: {getCurrentDate()}</p>
        </div>

        <div className="privacy-content">
          {/* Introduction */}
          <div className="policy-section">
            <h2>1. Introduction</h2>
            <p>
              Welcome to YT DOWN, a free online YouTube and TikTok video downloader platform. 
              We are committed to protecting your privacy and ensuring a safe, secure experience 
              while using our video downloading services. This Privacy Policy explains how we 
              handle information when you use our YouTube video downloader and TikTok video 
              downloader services.
            </p>
            <p>
              YT DOWN respects your privacy and follows safe usage policies. We believe in 
              transparency and want you to understand exactly how our free online video downloader 
              works and what information (if any) is collected during your use of our service.
            </p>
            <p>
              By using YT DOWN's YouTube and TikTok video downloading services, you agree to the 
              terms outlined in this Privacy Policy. If you do not agree with our policies, please 
              do not use our service.
            </p>
          </div>

          {/* Information We Collect */}
          <div className="policy-section highlight-section">
            <h2>2. Information We Do NOT Collect</h2>
            <p>
              <strong>YT DOWN is designed with your privacy in mind. We want to make it absolutely 
              clear what we DO NOT collect:</strong>
            </p>
            <div className="info-grid">
              <div className="info-item">
                <span className="icon">❌</span>
                <div>
                  <h4>No Personal Data</h4>
                  <p>We do not collect, store, or process any personal information such as names, 
                  email addresses, phone numbers, or physical addresses.</p>
                </div>
              </div>
              <div className="info-item">
                <span className="icon">❌</span>
                <div>
                  <h4>No Video URLs</h4>
                  <p>We do not store or log the YouTube or TikTok video URLs that you paste into 
                  our downloader. Your video links are processed temporarily and immediately discarded.</p>
                </div>
              </div>
              <div className="info-item">
                <span className="icon">❌</span>
                <div>
                  <h4>No Downloaded Videos</h4>
                  <p>We do not save, store, or keep copies of any videos you download. All videos 
                  are processed in real-time and delivered directly to your device.</p>
                </div>
              </div>
              <div className="info-item">
                <span className="icon">❌</span>
                <div>
                  <h4>No IP Addresses</h4>
                  <p>We do not log or store your IP address or any other network identifiers that 
                  could be used to identify you.</p>
                </div>
              </div>
              <div className="info-item">
                <span className="icon">❌</span>
                <div>
                  <h4>No User Accounts</h4>
                  <p>Our service does not require registration or user accounts, so we have no 
                  user databases or login credentials.</p>
                </div>
              </div>
              <div className="info-item">
                <span className="icon">❌</span>
                <div>
                  <h4>No Download History</h4>
                  <p>We do not track or maintain a history of your downloads. Each download session 
                  is completely independent and anonymous.</p>
                </div>
              </div>
            </div>
          </div>

          {/* How the Service Works */}
          <div className="policy-section">
            <h2>3. How Our YouTube & TikTok Video Downloader Works</h2>
            <p>
              Understanding how our service works will help you see why we don't need to collect 
              any personal information:
            </p>
            <div className="process-steps">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h4>You Paste a Public Video URL</h4>
                  <p>
                    You copy a public YouTube or TikTok video link and paste it into our downloader. 
                    This URL is only used temporarily to fetch the video information.
                  </p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h4>Real-Time Processing</h4>
                  <p>
                    Our system processes the URL in real-time, fetching the video data directly 
                    from YouTube or TikTok's public servers. This happens instantly and temporarily.
                  </p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h4>Direct Download to Your Device</h4>
                  <p>
                    The video is delivered directly to your device. We act as a bridge between 
                    you and the video source, without storing anything on our servers.
                  </p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">4</div>
                <div className="step-content">
                  <h4>No Data Retention</h4>
                  <p>
                    Once the download is complete, all temporary data is immediately discarded. 
                    Nothing is saved, logged, or stored on our servers.
                  </p>
                </div>
              </div>
            </div>
            <p>
              <strong>Important:</strong> All processing happens in real-time. We do not maintain 
              any databases of URLs, videos, or user information. Your privacy is protected by 
              design.
            </p>
          </div>

          {/* Cookies & Tracking */}
          <div className="policy-section">
            <h2>4. Cookies and Tracking Technologies</h2>
            <p>
              YT DOWN uses minimal cookies and tracking technologies to ensure the basic 
              functionality of our YouTube and TikTok video downloader service:
            </p>
            <h3>Essential Cookies</h3>
            <p>
              We may use essential cookies that are necessary for the website to function properly. 
              These cookies do not store any personal information and are used only for:
            </p>
            <ul>
              <li>Maintaining your session while using the downloader</li>
              <li>Remembering your language preference (if applicable)</li>
              <li>Ensuring the website loads correctly</li>
            </ul>
            <h3>Analytics (Optional)</h3>
            <p>
              We may use anonymous analytics tools (such as Google Analytics) to understand how 
              users interact with our website. These tools collect:
            </p>
            <ul>
              <li>General usage statistics (page views, session duration)</li>
              <li>Browser type and device information</li>
              <li>Geographic location (country/city level only)</li>
            </ul>
            <p>
              <strong>Important:</strong> Even if analytics are used, they are configured to 
              anonymize IP addresses and do not collect personally identifiable information. 
              You can disable cookies in your browser settings at any time.
            </p>
          </div>

          {/* Third-Party Services */}
          <div className="policy-section">
            <h2>5. Third-Party Services and APIs</h2>
            <p>
              To provide our YouTube and TikTok video downloading services, we may use third-party 
              APIs and services:
            </p>
            <h3>Video Platform APIs</h3>
            <p>
              Our service fetches publicly available video information from YouTube and TikTok 
              using their public APIs or similar methods. When you use our downloader:
            </p>
            <ul>
              <li>We access only publicly available video data</li>
              <li>We do not access private or restricted content</li>
              <li>We do not require your YouTube or TikTok login credentials</li>
              <li>We do not interact with your personal accounts on these platforms</li>
            </ul>
            <h3>Content Ownership</h3>
            <p>
              <strong>YT DOWN does not claim ownership of any videos downloaded through our service.</strong> 
              All videos remain the property of their original creators and copyright holders. We 
              are simply a tool that helps you download publicly available content.
            </p>
            <h3>Third-Party Links</h3>
            <p>
              Our website may contain links to third-party websites (such as YouTube and TikTok). 
              We are not responsible for the privacy practices of these external sites. We encourage 
              you to review their privacy policies.
            </p>
          </div>

          {/* User Responsibility */}
          <div className="policy-section highlight-section">
            <h2>6. User Responsibility and Copyright Compliance</h2>
            <p>
              <strong>Important Legal Notice:</strong> While YT DOWN provides a free YouTube and 
              TikTok video downloader service, users must use it responsibly and legally.
            </p>
            <h3>Your Responsibilities:</h3>
            <ul>
              <li>
                <strong>Respect Copyright Laws:</strong> You must comply with all applicable 
                copyright laws and regulations. Only download videos for which you have permission 
                or that are in the public domain.
              </li>
              <li>
                <strong>Personal Use Only:</strong> Our service is intended for personal, 
                non-commercial use. Do not use downloaded videos for commercial purposes without 
                proper authorization.
              </li>
              <li>
                <strong>Respect Content Creators:</strong> Always respect the rights of content 
                creators. If you plan to use downloaded content, obtain proper permissions.
              </li>
              <li>
                <strong>Follow Platform Terms:</strong> Ensure your use of downloaded content 
                complies with YouTube's and TikTok's Terms of Service.
              </li>
            </ul>
            <h3>Disclaimer:</h3>
            <p>
              <strong>YT DOWN is not responsible for any misuse of downloaded content.</strong> 
              Users are solely responsible for ensuring their use of our YouTube video downloader 
              and TikTok video downloader services complies with all applicable laws and regulations. 
              We do not endorse or encourage copyright infringement.
            </p>
          </div>

          {/* Security */}
          <div className="policy-section">
            <h2>7. Security Measures</h2>
            <p>
              Although we do not collect or store personal data, we still take security seriously:
            </p>
            <h3>Secure Connection</h3>
            <ul>
              <li>
                <strong>HTTPS Encryption:</strong> Our website uses HTTPS protocol to ensure all 
                data transmitted between your browser and our servers is encrypted.
              </li>
              <li>
                <strong>Secure Processing:</strong> All video processing happens through secure 
                channels to protect your download requests.
              </li>
            </ul>
            <h3>No Data Storage</h3>
            <ul>
              <li>
                <strong>Zero Data Retention:</strong> Since we don't store any user data, there's 
                no risk of your information being compromised in a data breach.
              </li>
              <li>
                <strong>Temporary Processing:</strong> All data is processed in memory and 
                immediately discarded after each download session.
              </li>
            </ul>
            <h3>Safe Downloads</h3>
            <ul>
              <li>
                <strong>No Malware:</strong> We do not inject any malware, viruses, or unwanted 
                software into downloaded videos.
              </li>
              <li>
                <strong>Direct Downloads:</strong> Videos are downloaded directly from YouTube 
                and TikTok servers, ensuring authenticity.
              </li>
            </ul>
          </div>

          {/* Children's Privacy */}
          <div className="policy-section">
            <h2>8. Children's Privacy</h2>
            <p>
              YT DOWN's YouTube and TikTok video downloader service is not intended for use by 
              children under the age of 13. We do not knowingly collect personal information from 
              children under 13.
            </p>
            <p>
              If you are a parent or guardian and believe your child has used our service, please 
              note that we do not collect or store any information from users, including children. 
              However, we recommend supervising children's internet usage and educating them about 
              online safety and copyright laws.
            </p>
            <p>
              If you have concerns about children's privacy, please contact us at 
              ramsis.nagham@outlook.com
            </p>
          </div>

          {/* International Users */}
          <div className="policy-section">
            <h2>9. International Users</h2>
            <p>
              YT DOWN is accessible worldwide and can be used to download YouTube and TikTok videos 
              from any country. However, please note:
            </p>
            <ul>
              <li>
                Our service complies with international privacy standards and best practices.
              </li>
              <li>
                Since we do not collect personal data, data transfer regulations (such as GDPR) 
                are not applicable to our service.
              </li>
              <li>
                Users are responsible for ensuring their use of our service complies with local 
                laws and regulations in their country.
              </li>
            </ul>
          </div>

          {/* Changes to Privacy Policy */}
          <div className="policy-section">
            <h2>10. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our 
              practices, technology, legal requirements, or other factors. When we make changes:
            </p>
            <ul>
              <li>
                We will update the "Last Updated" date at the top of this page.
              </li>
              <li>
                Significant changes will be prominently displayed on our website.
              </li>
              <li>
                Continued use of our YouTube and TikTok video downloader service after changes 
                constitutes acceptance of the updated Privacy Policy.
              </li>
            </ul>
            <p>
              We encourage you to review this Privacy Policy periodically to stay informed about 
              how we protect your privacy.
            </p>
          </div>

          {/* Contact Information */}
          <div className="policy-section contact-section">
            <h2>11. Contact Us</h2>
            <p>
              If you have any questions, concerns, or requests regarding this Privacy Policy or 
              our YouTube and TikTok video downloader service, please contact us:
            </p>
            <div className="contact-info">
              <div className="contact-method">
                <span className="contact-icon">📧</span>
                <div>
                  <h4>Email</h4>
                  <p>ramsis.nagham@outlook.com</p>
                </div>
              </div>
              <div className="contact-method">
                <span className="contact-icon">🌐</span>
                <div>
                  <h4>Website</h4>
                  <p>Visit our Contact page for more support options</p>
                </div>
              </div>
            </div>
            <p>
              We will respond to all privacy-related inquiries within 48 hours.
            </p>
          </div>

          {/* Summary */}
          <div className="policy-section summary-section">
            <h2>Privacy Policy Summary</h2>
            <p><strong>In simple terms:</strong></p>
            <div className="summary-grid">
              <div className="summary-item">
                <span className="summary-icon">✅</span>
                <p>We don't collect your personal information</p>
              </div>
              <div className="summary-item">
                <span className="summary-icon">✅</span>
                <p>We don't store video URLs or downloads</p>
              </div>
              <div className="summary-item">
                <span className="summary-icon">✅</span>
                <p>Everything is processed in real-time</p>
              </div>
              <div className="summary-item">
                <span className="summary-icon">✅</span>
                <p>Your privacy is protected by design</p>
              </div>
              <div className="summary-item">
                <span className="summary-icon">✅</span>
                <p>We use secure HTTPS connections</p>
              </div>
              <div className="summary-item">
                <span className="summary-icon">✅</span>
                <p>You're responsible for copyright compliance</p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="privacy-cta">
            <h2>Ready to Download?</h2>
            <p>
              Now that you understand our privacy practices, feel free to use our free YouTube 
              and TikTok video downloader with confidence.
            </p>
            <a href="/" className="cta-button">Start Downloading</a>
          </div>
        </div>
      </div>
    </section>
    </>
  );
};

export default Privacy;
